import React, { useState, useMemo, useRef } from "react";
import {
  Container,
  Grid,
  Typography,
  Box,
  Avatar,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import TrackChangesIcon from "@mui/icons-material/TrackChanges";
import InsightsIcon from "@mui/icons-material/Insights";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";

// Keyframe animations for floating dots
const floatingDotsAnimation = `
  @keyframes floatDot {
    0%, 100% {
      transform: translate(0, 0);
    }
    25% {
      transform: translate(10px, -10px);
    }
    50% {
      transform: translate(-5px, -20px);
    }
    75% {
      transform: translate(-10px, -10px);
    }
  }
`;

const HowItWorks = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const containerRef = useRef(null);
  const [isHovered, setIsHovered] = useState(false);
  const [mousePosition, setMousePosition] = useState({ x: 50, y: 50 });

  const handleMouseMove = (e) => {
    if (containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect();
      setMousePosition({
        x: ((e.clientX - rect.left) / rect.width) * 100,
        y: ((e.clientY - rect.top) / rect.height) * 100,
      });
    }
  };

  // Generate random dots with useMemo to prevent regeneration on every render
  const randomDots = useMemo(() => {
    const dots = [];
    const dotCount = 80; // Number of dots

    for (let i = 0; i < dotCount; i++) {
      dots.push({
        id: i,
        left: `${Math.random() * 100}%`,
        top: `${Math.random() * 100}%`,
        size: Math.random() * 3 + 2, // Random size between 2-5px
        animationDelay: `${Math.random() * 5}s`,
        animationDuration: `${Math.random() * 3 + 4}s`, // 4-7 seconds
        opacity: Math.random() * 0.4 + 0.4, // 0.4-0.8 opacity
      });
    }
    return dots;
  }, []);

  const steps = [
    {
      number: 1,
      icon: <CalendarMonthIcon sx={{ fontSize: 48 }} />,
      title: "Assign Schedules",
      description:
        "Set up shifts, assign staff to locations, and define working hours. Our smart system detects conflicts automatically.",
    },
    {
      number: 2,
      icon: <TrackChangesIcon sx={{ fontSize: 48 }} />,
      title: "Track Activity",
      description:
        "Monitor real-time check-ins, GPS locations, and delays. Get instant notifications when issues arise.",
    },
    {
      number: 3,
      icon: <InsightsIcon sx={{ fontSize: 48 }} />,
      title: "Analyze Performance",
      description:
        "Review comprehensive reports, track KPIs, and optimize workforce efficiency with data-driven insights.",
    },
  ];

  return (
    <Box
      ref={containerRef}
      id="how-it-works"
      onMouseMove={handleMouseMove}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      sx={{
        py: { xs: 8, md: 12 },
        backgroundColor: "background.paper",
        position: "relative",
        overflow: "hidden",
      }}
    >
      {/* Inject CSS animations */}
      <style>{floatingDotsAnimation}</style>

      {/* Random Floating Dots */}
      {randomDots.map((dot) => {
        // Calculate distance from mouse for interactive effect
        const dotX = parseFloat(dot.left);
        const dotY = parseFloat(dot.top);

        const distanceX = isHovered ? (mousePosition.x - dotX) * 1.5 : 0;
        const distanceY = isHovered ? (mousePosition.y - dotY) * 1.5 : 0;

        return (
          <Box
            key={dot.id}
            sx={{
              position: "absolute",
              left: dot.left,
              top: dot.top,
              width: `${dot.size}px`,
              height: `${dot.size}px`,
              borderRadius: "50%",
              backgroundColor: "rgba(111, 45, 189, 0.7)", // Darker purple
              opacity: dot.opacity,
              animation: `floatDot ${dot.animationDuration} ease-in-out infinite`,
              animationDelay: dot.animationDelay,
              transform: isHovered
                ? `translate(${distanceX}px, ${distanceY}px)`
                : "translate(0, 0)",
              transition: "transform 0.3s ease-out",
              zIndex: 0,
              pointerEvents: "none",
            }}
          />
        );
      })}

      {/* Additional darker accent dots */}
      {randomDots.slice(0, 30).map((dot) => {
        const dotX = parseFloat(dot.left);
        const dotY = parseFloat(dot.top);

        const distanceX = isHovered ? (mousePosition.x - dotX) * -1 : 0;
        const distanceY = isHovered ? (mousePosition.y - dotY) * -1 : 0;

        return (
          <Box
            key={`accent-${dot.id}`}
            sx={{
              position: "absolute",
              left: `${(parseFloat(dot.left) + 50) % 100}%`,
              top: `${(parseFloat(dot.top) + 50) % 100}%`,
              width: `${dot.size * 1.5}px`,
              height: `${dot.size * 1.5}px`,
              borderRadius: "50%",
              backgroundColor: "rgba(155, 93, 229, 0.6)", // Lighter purple
              opacity: dot.opacity * 0.8,
              animation: `floatDot ${dot.animationDuration} ease-in-out infinite`,
              animationDelay: `${parseFloat(dot.animationDelay) + 1}s`,
              transform: isHovered
                ? `translate(${distanceX}px, ${distanceY}px)`
                : "translate(0, 0)",
              transition: "transform 0.4s ease-out",
              zIndex: 0,
              pointerEvents: "none",
            }}
          />
        );
      })}

      <Container maxWidth="lg" sx={{ position: "relative", zIndex: 1 }}>
        {/* Section Header */}
        <Box sx={{ textAlign: "center", mb: 8 }}>
          <Typography
            variant="h3"
            sx={{
              mb: 2,
              fontWeight: 700,
              fontSize: { xs: "2rem", md: "2.5rem" },
            }}
          >
            Get Started in Three Simple Steps
          </Typography>
          <Typography variant="subtitle1" color="text.secondary">
            Start monitoring your workforce in minutes
          </Typography>
        </Box>

        {/* Steps */}
        {isMobile ? (
          // Mobile: Vertical Layout
          <Box sx={{ maxWidth: 400, mx: "auto" }}>
            {steps.map((step, index) => (
              <Box key={index} sx={{ mb: index < steps.length - 1 ? 6 : 0 }}>
                {/* Number Circle */}
                <Box sx={{ display: "flex", justifyContent: "center", mb: 2 }}>
                  <Avatar
                    sx={{
                      width: 80,
                      height: 80,
                      background: theme.palette.gradient.main,
                      fontSize: "2rem",
                      fontWeight: 700,
                    }}
                  >
                    {step.number}
                  </Avatar>
                </Box>

                {/* Icon */}
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    color: "primary.main",
                    mb: 2,
                  }}
                >
                  {step.icon}
                </Box>

                {/* Content */}
                <Box sx={{ textAlign: "center" }}>
                  <Typography
                    variant="h6"
                    sx={{
                      mb: 1.5,
                      fontWeight: 600,
                    }}
                  >
                    {step.title}
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ lineHeight: 1.7 }}
                  >
                    {step.description}
                  </Typography>
                </Box>
              </Box>
            ))}
          </Box>
        ) : (
          // Desktop: Horizontal Layout with Connectors
          <Grid container spacing={2} alignItems="center">
            {steps.map((step, index) => (
              <React.Fragment key={index}>
                {/* Step */}
                <Grid item xs={12} md={3}>
                  <Box sx={{ textAlign: "center" }}>
                    {/* Number Circle */}
                    <Box
                      sx={{ display: "flex", justifyContent: "center", mb: 2 }}
                    >
                      <Avatar
                        sx={{
                          width: 80,
                          height: 80,
                          background: theme.palette.gradient.main,
                          fontSize: "2rem",
                          fontWeight: 700,
                        }}
                      >
                        {step.number}
                      </Avatar>
                    </Box>

                    {/* Icon */}
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "center",
                        color: "primary.main",
                        mb: 2,
                      }}
                    >
                      {step.icon}
                    </Box>

                    {/* Content */}
                    <Typography
                      variant="h6"
                      sx={{
                        mb: 1.5,
                        fontWeight: 600,
                      }}
                    >
                      {step.title}
                    </Typography>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ lineHeight: 1.7 }}
                    >
                      {step.description}
                    </Typography>
                  </Box>
                </Grid>

                {/* Connector Arrow */}
                {index < steps.length - 1 && (
                  <Grid item xs={12} md={1}>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        height: "100%",
                      }}
                    >
                      <Box
                        sx={{
                          width: "100%",
                          height: 2,
                          background:
                            "linear-gradient(90deg, #9B5DE5 0%, #6F2DBD 100%)",
                          position: "relative",
                          "&::after": {
                            content: '""',
                            position: "absolute",
                            right: 0,
                            top: "50%",
                            transform: "translateY(-50%)",
                            width: 0,
                            height: 0,
                            borderLeft: "8px solid #6F2DBD",
                            borderTop: "6px solid transparent",
                            borderBottom: "6px solid transparent",
                          },
                        }}
                      />
                    </Box>
                  </Grid>
                )}
              </React.Fragment>
            ))}
          </Grid>
        )}

        {/* Call to Action */}
        <Box sx={{ textAlign: "center", mt: 8 }}>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
            Ready to streamline your workforce management?
          </Typography>
          <Box
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: 1,
              color: "primary.main",
              fontWeight: 600,
              cursor: "pointer",
              transition: "all 0.3s ease",
              "&:hover": {
                gap: 2,
              },
            }}
          >
            <Typography variant="body1" fontWeight={600}>
              Get started for free
            </Typography>
            <ArrowForwardIcon />
          </Box>
        </Box>
      </Container>
    </Box>
  );
};

export default HowItWorks;
